import { BlockStatus } from '@wiztime/core';

/** The items of the block context menu (2026-10b §6.1), numbered 1 to 7 there. */
export const ContextMenuItem = {
  EditText: 'editText',
  ChangeProject: 'changeProject',
  EditTime: 'editTime',
  Confirm: 'confirm',
  Overlay: 'overlay',
  ViewOverlap: 'viewOverlap',
  Delete: 'delete',
} as const;
export type ContextMenuItem = (typeof ContextMenuItem)[keyof typeof ContextMenuItem];

/** What decides which items apply to a block group. */
export interface ContextMenuTarget {
  readonly status: BlockStatus;
  readonly memberCount: number;
  /** The group overlaps another confirmed block. */
  readonly overlapping: boolean;
}

/**
 * The menu's items in three sections (separators fall between sections). Inapplicable items
 * are left out, never greyed (§6.1): the time item needs a single block, "confirm" a draft,
 * "view overlaps" a confirmed block that overlaps another confirmed block.
 */
export function contextMenuSections(target: ContextMenuTarget): ContextMenuItem[][] {
  const edit: ContextMenuItem[] = [ContextMenuItem.EditText, ContextMenuItem.ChangeProject];
  if (target.memberCount === 1) edit.push(ContextMenuItem.EditTime);
  const act: ContextMenuItem[] = [];
  if (target.status === BlockStatus.Draft) act.push(ContextMenuItem.Confirm);
  act.push(ContextMenuItem.Overlay);
  if (target.overlapping && target.status === BlockStatus.Confirmed) act.push(ContextMenuItem.ViewOverlap);
  return [edit, act, [ContextMenuItem.Delete]];
}

/** Shortcut hints shown right of the labels (§6.1); not localised, they are key names. */
export function shortcutHint(item: ContextMenuItem, mac: boolean): string | null {
  switch (item) {
    case ContextMenuItem.EditText:
      return 'F2';
    case ContextMenuItem.ChangeProject:
      return 'Enter';
    case ContextMenuItem.Overlay:
      return mac ? '⌘Enter' : 'Ctrl+Enter';
    case ContextMenuItem.ViewOverlap:
      return 'Shift+Enter';
    case ContextMenuItem.Delete:
      return 'Del';
    default:
      return null;
  }
}

export interface MenuBox {
  readonly width: number;
  readonly height: number;
}
export interface Viewport {
  readonly width: number;
  readonly height: number;
}

/** Minimum distance to the viewport edge (§6.2). */
export const MENU_EDGE_PX = 8;
/** The gap between the pointer and the menu corner (§6.2). */
export const MENU_POINTER_GAP_PX = 2;

/**
 * Top-left corner of the menu (§6.2): at (x, y), which already lies 2px right of and below the
 * pointer; when the right or lower side does not fit it flips to the left or above the pointer;
 * always at least 8px from every viewport edge.
 */
export function placeMenu(x: number, y: number, box: MenuBox, viewport: Viewport): { left: number; top: number } {
  let left = x;
  if (left + box.width > viewport.width - MENU_EDGE_PX) left = x - MENU_POINTER_GAP_PX - box.width;
  let top = y;
  if (top + box.height > viewport.height - MENU_EDGE_PX) top = y - MENU_POINTER_GAP_PX - box.height;
  return {
    left: Math.max(MENU_EDGE_PX, Math.min(left, viewport.width - MENU_EDGE_PX - box.width)),
    top: Math.max(MENU_EDGE_PX, Math.min(top, viewport.height - MENU_EDGE_PX - box.height)),
  };
}
